import { roundSlugSchema } from "./roundSlugs";
import {
  isFunderReviewPath,
  workspaceProposalClipsInputForPath,
  workspaceRecommenderClipsInputForPath,
  workspaceMediaSearchTargeted,
  workspaceMeetingsInputForPath,
  workspaceVideoMediaInputForPath,
  type MeetingsInput,
  type ProposalClipsInput,
  type RecommenderClipsInput,
  type WorkspaceVideoMediaInput,
} from "./funderWorkspaceRoutes";

/*
Round pageloads used to spend ~250ms (warm cache; much more cold) between the
HTML shell arriving and the first tRPC request, because nothing can fetch until
the entry bundle has parsed and executed. The server knows the route at request
time, so the SPA fallback injects a tiny inline script (see
server/lib/appShell.ts) that starts the round-gating fetches immediately and
stashes the in-flight promises on `window.__earlyFetches`; the client's tRPC
fetch wrapper (client/earlyFetch.ts) then adopts a matching in-flight promise
instead of issuing a duplicate request.

Adoption is keyed on the exact request URL, so this module is the single
source of the URL shapes — shared by the server (to fire them) and asserted
against the real tRPC client's output in src/tests/earlyRoundFetches.test.ts
(so the two can't drift). Only queries that (a) need nothing beyond the URL to
construct and (b) go over an unbatched GET lane with a deterministic URL are
eligible; batched queries are excluded because a batch URL depends on which
operations happen to share a tick.
*/

/** Where the in-flight early fetches live until the client adopts them. */
export const EARLY_FETCHES_WINDOW_KEY = "__earlyFetches";

/** The path and raw query string from the request target Express receives. */
export function requestLocationFromUrl(requestUrl: string): {
  pathname: string;
  search: string;
} {
  const queryIndex = requestUrl.indexOf("?");
  return queryIndex === -1
    ? { pathname: requestUrl, search: "" }
    : {
        pathname: requestUrl.slice(0, queryIndex),
        search: requestUrl.slice(queryIndex),
      };
}

// Round ids are "r-" + nanoid over [A-Za-z0-9_-] (see idGeneration.ts). The
// strict character set doubles as the safety gate for embedding the id in the
// injected inline script: none of these characters can escape a JS string or
// close a <script> tag.
const ROUND_PATH_PATTERN = /^\/round\/(r-[A-Za-z0-9_-]+)(?:\/|$)/;

export function roundIdFromPathname(pathname: string): string | null {
  return ROUND_PATH_PATTERN.exec(pathname)?.[1] ?? null;
}

/** Address syntax only, for static asset hints. Never use as an API round ID. */
export function roundAddressFromPathname(pathname: string): string | null {
  const id = roundIdFromPathname(pathname);

  if (id) {
    return id;
  }

  const address = /^\/round\/([^/]+)(?:\/|$)/.exec(pathname)?.[1];
  return roundSlugSchema.safeParse(address).success ? address! : null;
}

/**
 * Paths where a funder is (or is about to be) in the workspace: any
 * `/round/<id>/funder...` URL, plus the canonical round root — funders land
 * on `/round/<id>` constantly (bookmarks, home links, the iPad reopen path)
 * and are redirected into the workspace once bootstrap resolves their role.
 * Shared by the server shell (chunk preloads) and the client's initial-path
 * chunk warm so the two can never drift.
 */
export function isFunderLandingPath(pathname: string): boolean {
  const roundId = roundAddressFromPathname(pathname);

  if (roundId == null) {
    return false;
  }

  const rest = pathname.slice(`/round/${roundId}`.length);
  return rest === "" || rest === "/" || /^\/funder(?:\/|$)/.test(rest);
}

/**
 * Whether the round load may run proposal-text hydration deferred (unawaited)
 * for this URL — true exactly when the load lands in the funder workspace,
 * which renders no proposal field responses (see the gate in
 * client/pages/round/Round.tsx). Two ways to know that:
 *
 * - Any Review URL: the pathname alone proves the destination, whoever the
 *   viewer is.
 * - Any funder landing path when the viewer is a funder of an SFC round: the
 *   round root and bare /funder are established funder entry routes that
 *   redirect straight into the workspace (Review — or Decide for restricted
 *   funders), and the remaining /funder/* routes are the workspace itself.
 *   Non-funders on those same URLs redirect to proposal-rendering surfaces
 *   (as does a non-SFC round's root, which goes to /proposals for everyone),
 *   so they keep the awaited gate.
 */
export function roundLoadDefersFieldResponses(
  roundId: string,
  pathname: string,
  round: { viewerIsFunder: boolean; isSfcRound: boolean },
): boolean {
  if (isFunderReviewPath(roundId, pathname)) {
    return true;
  }

  return (
    round.viewerIsFunder &&
    round.isSfcRound &&
    roundIdFromPathname(pathname) === roundId &&
    isFunderLandingPath(pathname)
  );
}

/** `/round/<id>/funder/decide...` — the Decide tab's own chunk is warranted. */
export function isFunderDecidePath(pathname: string): boolean {
  const roundId = roundAddressFromPathname(pathname);

  if (roundId == null) {
    return false;
  }

  return /^\/funder\/decide(?:\/|$)/.test(
    pathname.slice(`/round/${roundId}`.length),
  );
}

/**
 * The GET URL tRPC's unbatched `httpLink` generates for a query — same
 * procedure path, same input encoding. Byte-equality with the real client is
 * what lets an early fetch be adopted instead of duplicated, and is locked by
 * unit test; don't "improve" the encoding here.
 */
export function trpcQueryGetUrl(procedure: string, input: unknown): string {
  return `/api/trpc/${procedure}?input=${encodeURIComponent(
    JSON.stringify(input),
  )}`;
}

/**
 * The `fetch` priority the shell should fire an early-fetch URL with, or null
 * for the default. Only getFieldResponses is demoted: its body is the load's
 * largest by ~3 orders of magnitude, and everything else in the list gates
 * the first render (see its bullet below). Unknown-`priority` browsers ignore
 * the option (an unrecognized RequestInit member), so this can only help.
 */
export function earlyFetchPriority(url: string): "low" | null {
  return url.startsWith("/api/trpc/getFieldResponses?") ? "low" : null;
}

/**
 * The early-fetchable request URLs for a round pathname, or an empty list for
 * everything else (non-round routes serve the untouched shell).
 *
 * - getRoundBootstrap gates the whole workspace render on cache-first rounds.
 *   Fired for every round route: whether a round is cache-first is partly
 *   client-local (learned aggregate sizes in localStorage), so the server
 *   can't decide it; a non-cache-first load just never adopts the entry.
 * - getFieldResponses (the full-round form) is the load's single largest
 *   response (~900KB wire on the live round) and needs only the round id: the
 *   round load prefetches every current field response on cold clients (see
 *   ClientInfoStore.prefetchAllCurrentIfCold in Round.tsx), so firing it here
 *   moves the biggest download to the front of the connection. Measured on
 *   the live-round fixture at 5Mbps: funder cold load 5.95s -> 4.95s. It is
 *   fired at LOW fetch priority (see earlyFetchPriority): all the early
 *   fetches share one H2 connection, and at default priority its megabyte
 *   body head-of-line-blocked the tiny render-gating responses below —
 *   measured +430ms on their headers at 170ms RTT. Low keeps it starting at
 *   time zero but yields the connection to the small queries.
 *   SKIPPED on Review URLs: the funder workspace renders no proposal field
 *   responses (nothing on the route reads them — see the deferral gate in
 *   Round.tsx), and even at low priority the megabyte body occupies the
 *   pageload's connection alongside the lead video, subtitles, and corpus
 *   payloads (1.33MB compressed on the live round's production trace). The
 *   round load hydrates the store after the pageload settles instead. Funder
 *   entry routes that merely REDIRECT into the workspace (round root, bare
 *   /funder) keep the fetch: the pathname alone can't prove the viewer is a
 *   funder, and non-funders on those URLs land on proposal-rendering
 *   surfaces.
 * - getUserVideoState supplies saved/progress state to every round video and
 *   needs only the round id, so it can ride the same HTML-started lane.
 * - The media queries gate their respective workspace surfaces and fire only
 *   when the URL contains enough information to build the exact mounted input.
 *   Their shared URL parsers are also used by the client startup prefetches.
 */
export function earlyRoundFetchUrls(requestUrl: string): string[] {
  const { pathname, search } = requestLocationFromUrl(requestUrl);
  const roundId = roundIdFromPathname(pathname);

  if (roundId == null) {
    return [];
  }

  const urls = [trpcQueryGetUrl("getRoundBootstrap", { roundId })];

  // See the getFieldResponses bullet above: skipped exactly on Review URLs.
  if (!isFunderReviewPath(roundId, pathname)) {
    urls.push(trpcQueryGetUrl("getFieldResponses", { roundId }));
  }

  urls.push(trpcQueryGetUrl("getUserVideoState", { roundId }));
  const meetingsInput: MeetingsInput | null = workspaceMeetingsInputForPath(
    roundId,
    pathname,
  );

  if (meetingsInput != null) {
    urls.push(trpcQueryGetUrl("getMeetings", meetingsInput));
  }

  const proposalClipsInput: ProposalClipsInput | null =
    workspaceProposalClipsInputForPath(roundId, pathname);

  if (proposalClipsInput != null) {
    urls.push(
      trpcQueryGetUrl("getProposalTranscriptClips", proposalClipsInput),
    );
  }

  const recommenderClipsInput: RecommenderClipsInput | null =
    workspaceRecommenderClipsInputForPath(roundId, pathname);

  if (recommenderClipsInput != null) {
    urls.push(
      trpcQueryGetUrl("getRecommenderTranscriptClips", recommenderClipsInput),
    );
  }

  // A query-string target overrides the rail's lead selection. Its actual
  // video id cannot be derived from the pathname, so the mounted player uses a
  // direct `{videoId}` query and cannot adopt this context-shaped request.
  if (!workspaceMediaSearchTargeted(search)) {
    const videoMediaInput: WorkspaceVideoMediaInput | null =
      workspaceVideoMediaInputForPath(roundId, pathname);

    if (videoMediaInput != null) {
      urls.push(trpcQueryGetUrl("getVideoMediaUrls", videoMediaInput));
    }
  }

  return urls;
}
